import type { ReactNode } from "react";
import { AppMenu } from "./AppMenu";
import { BackLink } from "./BackLink";
import { SignalerProbleme } from "./SignalerProbleme";

interface AppHeaderBaseProps {
  /** true = BackLink par défaut ; objet = props relayées à BackLink. */
  back?: boolean | { to?: string; label?: string };
  /** Actions contextuelles (variante compacte), rendues AVANT le burger. */
  actions?: ReactNode;
  /** false = pas de burger (pré-auth). Défaut true. */
  menu?: boolean;
}

/** Variantes avec rangée identité : le titre est obligatoire (jamais de h1 vide). */
interface AppHeaderIdentityProps extends AppHeaderBaseProps {
  variant?: "full" | "plain";
  title: string;
  subtitle?: ReactNode;
  /** Sur-titre (pages légales). */
  eyebrow?: ReactNode;
  /** Contenu additionnel sous le sous-titre (ex. ProjectContextBadge). */
  children?: ReactNode;
}

/** Variante compacte : barre d'app seule, aucune identité. */
interface AppHeaderCompactProps extends AppHeaderBaseProps {
  variant: "compact";
}

type AppHeaderProps = AppHeaderIdentityProps | AppHeaderCompactProps;

// En-tête canonique (DS §6.8) — deux rangées : barre d'app (Retour à gauche,
// actions contextuelles puis burger à droite) et identité (flamme décorative,
// logo Flambeau, H1, sous-titre). Trois variantes : complète (défaut), compacte
// (barre seule — écrans de travail), muette (identité seule — pré-auth, capture
// avant `saved`). Le retour explicite à l'accueil vit dans le menu (entrée
// « Accueil ») — la flamme n'est pas une affordance. Jamais sticky.
export function AppHeader(props: AppHeaderProps) {
  const { back = false, actions, menu = true } = props;
  const isPlain = props.variant === "plain";
  const showNavRow = !isPlain && (back !== false || Boolean(actions) || menu);
  const backProps = typeof back === "object" ? back : {};

  return (
    <header className="flex flex-col gap-3 pt-2">
      {/* Amendement DS §6.8/§6.16 : en variante « plain », le picto « Signaler
          un problème » est la SEULE affordance de la barre — un <dialog>
          n'est pas une sortie de nav, il reste donc disponible même pendant
          un enregistrement (l'écran de capture reste en `plain` jusqu'à ce
          que la prise soit `saved`). */}
      {isPlain && (
        <div className="flex items-center justify-end">
          <SignalerProbleme />
        </div>
      )}
      {showNavRow && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {back !== false && <BackLink {...backProps} />}
          </div>
          <div className="flex flex-none items-center gap-1">
            {actions}
            <SignalerProbleme />
            {menu && <AppMenu />}
          </div>
        </div>
      )}
      {props.variant !== "compact" && (
        <>
          <span className="brand-flame">
            <img src="/flame-mark.svg" alt="" className="h-full w-full" />
          </span>
          <div className="flex flex-col gap-1">
            <img
              src="/flambeau-logo.svg"
              alt="Flambeau"
              className="h-4 w-auto self-start opacity-70"
            />
            {props.eyebrow != null && (
              <p className="m-0 font-sans text-label font-semibold uppercase tracking-[0.16em] text-flambeau-700">
                {props.eyebrow}
              </p>
            )}
            <h1 className="m-0 font-display text-screen font-extrabold tracking-tight text-ink">
              {props.title}
            </h1>
            {props.subtitle != null && (
              <p className="m-0 text-small text-muted">{props.subtitle}</p>
            )}
            {props.children}
          </div>
        </>
      )}
    </header>
  );
}
